import React from 'react';

// LRU-like Cache Implementation for Blob URLs
// Limits total memory usage to prevent leaks while keeping recent images fast.

interface CacheEntry {
  blobUrl: string;
  size: number;
  lastUsed: number;
}

// Configuration
const MAX_CACHE_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB
const TARGET_CACHE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB (Target size after cleanup)

// State
const blobCache = new Map<string, CacheEntry>();
const inflightRequests = new Map<string, Promise<string>>();
let currentCacheSize = 0;

/**
 * Enforces cache limits by removing least recently used items
 * until we are below the target size.
 */
const enforceCacheLimit = () => {
  if (currentCacheSize <= MAX_CACHE_SIZE_BYTES) return;

  // Convert map to array and sort by lastUsed (ascending = oldest first)
  const entries = Array.from(blobCache.entries()).sort(([, a], [, b]) => a.lastUsed - b.lastUsed);

  for (const [url, entry] of entries) {
    if (currentCacheSize <= TARGET_CACHE_SIZE_BYTES) break;

    // Revoke and remove
    URL.revokeObjectURL(entry.blobUrl);
    blobCache.delete(url);
    currentCacheSize -= entry.size;
  }
};

export const fetchImageBlobUrl = async (url: string): Promise<string> => {
  try {
    // 1. Check Cache
    if (blobCache.has(url)) {
      const entry = blobCache.get(url)!;
      // Update lastUsed timestamp (LRU)
      entry.lastUsed = Date.now();
      return entry.blobUrl;
    }

    // 2. Check Inflight Requests
    if (inflightRequests.has(url)) {
      return inflightRequests.get(url)!;
    }

    // 3. Fetch (only http/https or relative)
    if (url.startsWith('http') || url.startsWith('/')) {
      const fetchPromise = (async () => {
        try {
          const fetchUrl = url.startsWith('/') && typeof window !== 'undefined' 
            ? `${window.location.origin}${url}` 
            : url;

          const res = await fetch(fetchUrl, { 
            cache: "no-store", 
            mode: "cors" 
          });
          
          if (!res.ok) {
            throw new Error(`Failed to fetch image: ${res.status} ${res.statusText}`);
          }
          
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          const size = blob.size;

          // Add to cache
          blobCache.set(url, {
            blobUrl,
            size,
            lastUsed: Date.now()
          });
          currentCacheSize += size;

          // Check limits
          enforceCacheLimit();

          return blobUrl;
        } finally {
          // Clean up inflight request tracker
          inflightRequests.delete(url);
        }
      })();

      inflightRequests.set(url, fetchPromise);
      return await fetchPromise;
    }
    return url;
  } catch (error) {
    console.warn('Failed to fetch image with cache-busting, falling back to direct URL:', error);
    return url;
  }
};

/**
 * Custom hook to load image with cache busting (blob url)
 * Replaces useImage from 'use-image' to support cache: "no-store"
 */
export const useCacheBustedImage = (url: string, crossOrigin?: string) => {
  const [image, setImage] = React.useState<HTMLImageElement | undefined>(undefined);
  const [status, setStatus] = React.useState<'loading' | 'loaded' | 'failed'>('loading');

  React.useEffect(() => {
    if (!url) return;

    let active = true;
    let objectUrl: string | null = null;
    const img = document.createElement('img');

    const load = async () => {
      try {
        setStatus('loading');
        // Fetch with cache-busting logic
        objectUrl = await fetchImageBlobUrl(url);
        
        if (!active) return;
        
        if (crossOrigin) img.crossOrigin = crossOrigin;
        
        img.onload = () => {
          if (!active) return;
          setImage(img);
          setStatus('loaded');
        };
        
        img.onerror = () => {
          if (!active) return;
          setImage(undefined);
          setStatus('failed');
        };
        
        img.src = objectUrl;
      } catch (e) {
        if (!active) return;
        setStatus('failed');
      }
    };

    load();

    return () => {
      active = false;
      // Do NOT revoke object URL here as it might be cached and shared
    };
  }, [url, crossOrigin]);

  return [image, status] as const;
};
